import { useCallback, useEffect, useMemo, useState } from "react";
import API from "../../../service/api";
import { useTranslation } from "react-i18next";
import { MDBBtn, MDBIcon } from "mdb-react-ui-kit";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { DataTable } from "../../../components/DataTable";
import { AdminPage } from "../AdminPage";
import { SplitScreenDiv } from "../../../components/SplitScreenDiv";
import { SubcompanyEdit } from "./SubcompanyEdit";
import { AddSubcompanyModal } from "./AddSubcompanyModal";

export const CompaniesPage = ({myCompany, canAdd, canEditSubcompany, canDeleteSubcompany, canAddUser, canViewUsers}) => {
    const [subcompanies, setSubcompanies] = useState<any[]>([]);
    const [countries, setCountries] = useState<any[]|null>([]);

    const [subcompaniesLoading, setSubcompaniesLoading] = useState(false);
    const [editCompanyId, setEditCompanyId] = useState<number|null>(null);
    const [addCompany, setAddCompany] = useState(false);
    
    const [deleteCompanyId, setDeleteCompanyId] = useState<number|null>(null);
    const [error, setError] = useState('');

    const { t } = useTranslation();

    const fetch = useCallback(() => {
        API.GET('/api/config/countries')
            .then(setCountries)
            .catch(err => setError(err.message));
        API.GET('/api/companies/subcompanies')
            .then(setSubcompanies)
            .catch(err => setError(err.message));
    }, []);

    useEffect(() => {
        fetch();
    }, []);

    const Table = useMemo(() => (
        <DataTable
            data={subcompanies}
            height={500}
            onRowSelected={c => setEditCompanyId(c.id)}
            columns={[
                { headerName: t('name'), field: 'name', flex: 3, cellRenderer: ({value}) => (
                    <span>
                        <MDBIcon icon="building" color="primary" className="me-1"/>
                        {value}
                    </span>
                ) },
                { headerName: t('country'), field: 'country', flex: 2, cellRenderer: ({value}) => (
                    <span>
                        <span className={`flag-icon flag-icon-${value?.toLowerCase()} me-1`}></span>
                        <span>{countries?.find(c => c.code == value)?.country}</span>
                    </span>
                )},
                { headerName: t('cif'), field: 'CIF', flex: 2 },
                { headerName: t('permissions'), valueGetter: ({data}) => data.maxRights?.Name, flex: 2 }
            ]}
        />
    ), [subcompanies, countries]);

    const SubcompanyEditScreen = useMemo(() => editCompanyId == null ? null : (
        <SubcompanyEdit
            companyId={editCompanyId}
            canAddUser={canAddUser}
            canDeleteSubcompany={canDeleteSubcompany}
            canEditSubcompany={canEditSubcompany}
            canViewUsers={canViewUsers}
            onClose={() => {setEditCompanyId(null)}}
            onEditDone={fetch}
            onDelete={() => setDeleteCompanyId(editCompanyId)}
        />
    ), [editCompanyId, canAddUser, canDeleteSubcompany, canEditSubcompany, canViewUsers]);

    if (!myCompany || !countries) return null;

    return (
        <AdminPage
            title={t('subcompanies')}
            header={!canAdd ? null : (
                <MDBBtn color="link" onClick={() => { setAddCompany(true); }}>
                    <MDBIcon icon="plus-circle" className="me-1"/>
                    {t('add_subcompany')}
                </MDBBtn>
            )}
        >
            <AddSubcompanyModal
                open={addCompany}
                countries={countries}
                onClose={() => setAddCompany(false)}
                onAdd={fetch}
            />
            <DeleteConfirmModal
                open={deleteCompanyId !== null}
                title={t('remove_company')}
                message={t('remove_company_prompt')}
                onCancel={() => setDeleteCompanyId(null)}
                onConfirm={() => {
                    API.DELETE('/api/companies/subcompanies/'+deleteCompanyId)
                        .then(() => {
                            setEditCompanyId(null);
                            fetch();
                        })
                        .catch(err => {
                            setError(err.message);
                        })
                    setDeleteCompanyId(null);
                }}
            />
            <SplitScreenDiv
                ratio={60}
                left={Table}
                right={SubcompanyEditScreen}
            />
        </AdminPage>
    )
}